'use client'
import { type FC, useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { MODALS } from '@/context'
import { useDeviceType, useModal } from '@/hooks'
import type { TypeCmsLayout, TypeCmsLayoutLayoutItem } from '@/schema'
import { designGrid, mq, vw } from '@/styles'
import { Menu } from '../commons/layout/Menu'
import { Logo } from '../commons/layout/Logo'
import { getIcon } from '@/utils'
import { Img } from '../commons'
import { usePathname } from 'next/navigation'

type Props = {
  data: TypeCmsLayout['navbar']
  navbarHeights: {
    mobile: number
    desktop: number
  }
}

export const ModalMenu:FC<Props> = ({ navbarHeights, data }) => {
  const { isActive, updatePayload } = useModal(MODALS.MENU)
  const pathname = usePathname()
  const ref = useRef<HTMLDialogElement>(null)
  const [levelLinks, setLevelLinks] = useState<TypeCmsLayoutLayoutItem[] | null>(null)
  const { isMobile } = useDeviceType()

  const onClose = useCallback(() => {
    updatePayload({ open: false, payload: null, close: true })
  }, [updatePayload])

  const onClick = e => {
    // Click on backdrop
    if (e.target.tagName === 'DIALOG') onClose()
  }

  // const onClickSearch = e => {
  //   updatePayload({ open: false, payload: null, close: true })
  // }

  useEffect(() => {
    ref.current?.[isActive ? 'showModal' : 'close']()
  }, [isActive])

  useEffect(() => {
    onClose()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return (
    <Dialog ref={ref} id={MODALS.MENU} aria-labelledby='dialog-title' $navbarHeights={navbarHeights} onClick={onClick} onCancel={onClose}>
      <div className='dialog-content'>
        <header>
          <h2 id='dialog--menu-title' className='srt-only'>Menu</h2>
          <button className='btn--menu' onClick={levelLinks && isMobile ? () => { setLevelLinks(null) } : onClose}>
            <Img media={getIcon(levelLinks && isMobile ? 'arrow--left' : 'close')} />
          </button>
          <Logo color='var(--color-text)' />
          {/* <button className='btn--search-trigger' onClick={onClickSearch}>
            <Img media={getIcon('search')} />
          </button> */}
        </header>
        <main>
          <Menu data={data} levelLinks={levelLinks} setLevelLinks={setLevelLinks} />
        </main>
      </div>
    </Dialog>
  )
}

const Dialog = styled.dialog<{ $navbarHeights: Props['navbarHeights'] }>`
  align-items: flex-start;
  background-color: transparent;
  border: 0;
  box-sizing: content-box;
  color: inherit;
  display: flex;
  left: 0;
  justify-content: center;
  margin: 0;
  max-height: 100vh;
  max-height: 100dvh;
  max-width: unset;
  opacity: 0;
  padding: 0;
  overflow-y: auto;
  pointer-events: none;
  position: fixed;
  right: 0;
  transform: translateX(-100%);
  transition: 250ms opacity ease-in-out, 300ms transform ease-in-out;
  width: 100vw;
  width: 100dvw;
  z-index: 5000;

  ${mq.greaterThan('tablet')} {
    transform: translateX(0);
  }

  /* &,
  &::backdrop {
    max-height: calc(100vh - ${({ $navbarHeights }) => $navbarHeights.mobile}px);
    max-height: calc(100dvh - ${({ $navbarHeights }) => $navbarHeights.mobile}px);
    top: ${({ $navbarHeights }) => $navbarHeights.mobile}px;

    ${mq.greaterThan('mobile')} {
      max-height: calc(100vh - ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'mobile')});
      max-height: calc(100dvh - ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'mobile')});
      top: ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'mobile')};
    }

    ${mq.greaterThan('nexus7')} {
      max-height: calc(100vh - ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'tablet')});
      max-height: calc(100dvh - ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'tablet')});
      top: ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      max-height: calc(100vh - ${({ $navbarHeights }) => vw($navbarHeights.desktop, 'desktop')});
      max-height: calc(100dvh - ${({ $navbarHeights }) => vw($navbarHeights.desktop, 'desktop')});
      top: ${({ $navbarHeights }) => vw($navbarHeights.desktop, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      max-height: calc(100vh - ${({ $navbarHeights }) => $navbarHeights.desktop}px);
      max-height: calc(100dvh - ${({ $navbarHeights }) => $navbarHeights.desktop}px);
      top: ${({ $navbarHeights }) => $navbarHeights.desktop}px;
    }
  } */

  &::backdrop {
    background: var(--color-black);
    height: 100%;
    opacity: 0;
    position: fixed;
    transition: 250ms opacity ease-in-out 5000ms;
    transition-delay: 5000ms;
    width: 100vw;
    width: 100dvw;
  }

  &[open] {
    opacity: 1;
    pointer-events: auto;
    transform: translateX(0);

    &::backdrop {
      opacity: .6;
      pointer-events: auto;
    }

    header {
      opacity: 1;
    }
  }

  .dialog-content {
    background: var(--color-background);
    box-sizing: border-box;
    display: block;
    height: auto;
    min-height: 100vh;
    min-height: 100dvh;
    /* min-height: calc(100vh - ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'mobile')});
    min-height: calc(100dvh - ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'mobile')}); */
    width: 100vw;
    width: 100dvw;

    /* ${mq.greaterThan('nexus7')} {
      min-height: calc(100vh - ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'tablet')});
      min-height: calc(100dvh - ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'tablet')});
    }

    ${mq.greaterThan('tablet')} {
      min-height: calc(100vh - ${({ $navbarHeights }) => vw($navbarHeights.desktop, 'desktop')});
      min-height: calc(100dvh - ${({ $navbarHeights }) => vw($navbarHeights.desktop, 'desktop')});
    }

    ${mq.greaterThan('desktop')} {
      min-height: calc(100vh - ${({ $navbarHeights }) => $navbarHeights.desktop}px);
      min-height: calc(100dvh - ${({ $navbarHeights }) => $navbarHeights.desktop}px);
    } */
  }

  header {
    ${designGrid({})}
    align-items: center;
    left: 0;
    justify-content: center;
    opacity: 0;
    position: sticky;
    top: 0;
    transition: 100ms opacity ease-in-out .3s;
    width: 100%;
    height: ${({ $navbarHeights }) => $navbarHeights.mobile}px;

    ${mq.greaterThan('mobile')} {
      height: ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'mobile')};
    }

    ${mq.greaterThan('nexus7')} {
      height: ${({ $navbarHeights }) => vw($navbarHeights.mobile, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${({ $navbarHeights }) => vw($navbarHeights.desktop, 'desktop')};
      opacity: 1;
    }

    ${mq.greaterThan('desktop')} {
      height: ${({ $navbarHeights }) => $navbarHeights.desktop}px;
    }
  }

  .btn--menu {
    align-items: center;
    grid-column: 1 / span 2;
    grid-row: 1 / span 1;
    height: 100%;
    justify-content: center;
    padding: 0 5px;
    transform: translate(-15px);
    width: 50px;
  }

  .btn--search-trigger {
    grid-column: 6 / span 1;
    grid-row: 1 / span 1;
    height: 100%;
    justify-content: center;
    margin-left: auto;
    padding: 0 5px;
    transform: translate(10px);
    width: 50px;

    ${mq.greaterThan('tablet')} {
      grid-column: 12 / span 1;
    }
  }

  main {
    padding-top: 30px;
  }

  .btn--close {
    position: fixed
  }
`